import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import UserTaskListItemDto from "../../types/dtos/tasks/advanced-tasks/UserTaskListItemDto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faComment,
  faTag,
} from "@fortawesome/pro-regular-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { dateHelper } from "../../helpers";
import UserContext from "../../state/UserContext";
import listingHelper from "../../helpers/listingHelper";
import AdvTaskListingDto from "../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import { TaskManagementSectionViewer } from "../../types/dtos/tasks/advanced-tasks/TaskManagementSectionViewer";

interface TaskTabItemProps {
  task: AdvTaskListingDto;
  isCompletedTabSelected: boolean;
  isHistoricTabSelected: boolean;
  viewerType: TaskManagementSectionViewer;
  onClickEvent: (userTaskId: string) => void;
}

const TaskTabItem: React.FC<TaskTabItemProps> = ({
  task,
  isCompletedTabSelected,
  isHistoricTabSelected,
  viewerType,
  onClickEvent,
}) => {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const taskIsHighlightedForUser = (task: UserTaskListItemDto) => {
    if (task.highlightToOwner && task.ownerEmployeeId === userContext.user.id) {
      return true;
    }

    if (
      task.highlightToManager &&
      task.ownerEmployeeId !== userContext.user.id
    ) {
      return true;
    }

    return false;
  };

  const formattedTargetDate = task.targetDate
    ? dateHelper
        .storedTargetDateAsLocalCalendarDate(task.targetDate)
        .toLocaleDateString()
    : "Not available";

  const formattedCompletionDate = task.dateCompleted
    ? dateHelper.convertUtcDateToLocal(task.dateCompleted).toLocaleDateString()
    : "Not available";

  const clientColor = userContext?.user?.client?.primaryHexColour || "FF0000";

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent running twice (parent div has onClick as well as button)
    onClickEvent(task.userTaskId);
  };

  // Render the main task content
  const renderMainContent = () => {
    return (
      <div
        onClick={handleButtonClick}
        className={`flex flex-col sm:flex-row sm:justify-between sm:items-center w-full py-3 px-3 sm:px-4 relative cursor-pointer ${
          viewerType !== "ADMIN" ? listingHelper.getOwnerBorderClass(task) : ""
        }`}
      >
        <div className="flex flex-col items-start mb-2 sm:mb-0">
          <div className="flex items-center">
            <span className="font-medium text-base text-gray-700">
              {taskIsHighlightedForUser(task) && (
                <span
                  className="mr-1 h-2 w-2 rounded-full inline-block"
                  style={{ backgroundColor: `#${clientColor}` }}
                  title="Highlighted task"
                />
              )}
              {task.title}
            </span>
          </div>
          <div
            className="flex flex-col sm:flex-row sm:space-x-4 mt-1 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center text-sm text-gray-500">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="mr-1 text-gray-500"
                size="xs"
              />
              <span>Due: {formattedTargetDate}</span>
            </div>
            {task.categoryName && (
              <div className="flex items-center text-sm text-gray-500 mt-1 sm:mt-0">
                <FontAwesomeIcon
                  icon={faTag}
                  className="mr-1 text-gray-500"
                  size="xs"
                />
                <span>{t(task.categoryName)}</span>
              </div>
            )}
            {task.commentCount > 0 && (
              <div className="flex items-center text-sm text-gray-500 mt-1 sm:mt-0">
                <FontAwesomeIcon
                  icon={faComment}
                  className="mr-1 text-gray-500"
                  size="xs"
                />
                <span>Comments: {task.commentCount}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto">
          <span
            onClick={(e) => e.stopPropagation()}
            className={`mr-2 sm:mr-4 text-xs px-2 py-0.5 rounded-full cursor-default ${listingHelper.getTaskStatusColor(
              task,
            )}`}
          >
            {listingHelper.getTaskStatusText(task)}
          </span>

          <button
            className="btn-primary text-sm py-1 px-3 sm:px-4 w-20 sm:w-24 text-center"
            onClick={handleButtonClick}
          >
            {t("Common.View")}
          </button>
        </div>
      </div>
    );
  };

  // Render completed section (only when task is completed)
  const renderCompletedSection = () => {
    if (task.taskStatus !== "COMPLETED") {
      return null;
    }

    return (
      <div className="border-t border-gray-200 bg-white px-3 sm:px-4 py-2 text-sm text-gray-500">
        <div className="flex items-center">
          <span className="flex items-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="mr-1 text-green-600"
              size="sm"
            />
            Completed{" "}
            {task.completedByUserFullName
              ? `by ${task.completedByUserFullName} `
              : ""}
            on {formattedCompletionDate}
          </span>
          {task.isHistoric && task.reviewedInAnswerSetName && (
            <>
              <span className="mx-2 text-gray-300">|</span>
              <span>Reviewed in {task.reviewedInAnswerSetName}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-3 border-[0.5px] border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100/90 overflow-hidden">
      {renderMainContent()}
      {renderCompletedSection()}
    </div>
  );
};

export default TaskTabItem;
